'use client';

import { PropsWithChildren, useCallback, useMemo } from 'react';
import { ContentAssetKeys } from '@headless-workspace/core-domain';
import { Component } from '@headless-workspace/typings';
import { ContentAssetType, PopularSearchValue } from '../../../../server';
import { ClientConfigsRecord, ConfigKey } from './ClientConfigsRecord';
import { ClientDataContext, ClientDataContextValueType } from './ClientDataContext';
import { ClientEnvKey, ClientEnvKeysRecord } from './ClientEnvKeysRecord';
import { ClientFlagsRecord, ClientFlagsRecordKey } from './ClientFlagsRecord';

type ClientDataProviderProps = {
    clientConfigs?: ClientConfigsRecord;
    clientEnvs: ClientEnvKeysRecord;
    clientFlags: ClientFlagsRecord;
    popularSearches: PopularSearchValue[];
    contentAssets: Map<string, ContentAssetType | undefined>;
} & PropsWithChildren;

export const ClientDataProvider: Component<ClientDataProviderProps> = ({
    clientConfigs,
    clientEnvs,
    clientFlags,
    popularSearches,
    contentAssets,
    children,
}) => {
    const getClientEnvVariable = useCallback(
        (clientEnvKey: ClientEnvKey): string => {
            return clientEnvs[clientEnvKey];
        },
        [clientEnvs],
    );

    const getClientFlags = useCallback(
        (flagKey: ClientFlagsRecordKey): boolean => {
            return clientFlags[flagKey] ?? false;
        },
        [clientFlags],
    );

    const getClientConfigs = useCallback(
        <K extends ConfigKey>(configKey: K): ClientConfigsRecord[K] | undefined => {
            return clientConfigs ? clientConfigs[configKey] : undefined;
        },
        [clientConfigs],
    );

    const getContentAssets = useCallback(
        (contentAssetKey: ContentAssetKeys): ContentAssetType | undefined => {
            return contentAssets.get(contentAssetKey);
        },
        [contentAssets],
    );

    const providerValue: ClientDataContextValueType = useMemo(
        () => ({
            clientEnvs,
            getClientEnvVariable,
            getClientFlags,
            getClientConfigs,
            getContentAssets,
            clientValues: {
                popularSearches,
            },
        }),
        [clientEnvs, getClientEnvVariable, getClientConfigs, getClientFlags, getContentAssets, popularSearches],
    );

    return <ClientDataContext.Provider value={providerValue}>{children}</ClientDataContext.Provider>;
};
